<template>
  <div>
    <HeroHeader
      eyebrow="PROPIEDAD"
      :title="estate.description || '…'"
      :back="true"
      @back="$router.back()"
      :compact="true"
    >
      <template #toolbar>
        <button v-if="estate.id" class="btn btn--outline btn--sm"
                @click="$router.push({ name: 'tenants', query: { estateId: estate.id, estateName: estate.description } })">
          Expositores
        </button>
      </template>
    </HeroHeader>

    <!-- Meta: dirección / propietario / m² -->
    <div v-if="estate.id" class="tenant-detail-meta">
      <span v-if="estate.address" class="tenant-detail-meta__item">{{ estate.address }}</span>
      <span v-if="estate.address && estate.owner" class="tenant-detail-meta__sep">·</span>
      <span v-if="estate.owner" class="tenant-detail-meta__item">{{ estate.owner }}</span>
      <span v-if="(estate.address || estate.owner) && estate.m2" class="tenant-detail-meta__sep">·</span>
      <span v-if="estate.m2" class="tenant-detail-meta__item">{{ estate.m2 }} m²</span>
    </div>

    <TabBar :tabs="sectionTabs" v-model="activeTab"/>

    <!-- El mes es de la PÁGINA, no de cada pestaña: un solo control, en el mismo
         lugar, que manda sobre las cuatro. Antes Balance y Recaudación tenían el
         suyo, Gastos usaba pills y Impuestos otro selector con estado aparte.
         En Gastos e Impuestos lo dibuja la vista embebida —con `v-model:period`
         sobre este mismo dato— para que comparta fila con las CTAs de la pestaña. -->
    <MonthNav v-if="activeTab === 'balance' || activeTab === 'collection'" v-model="period"/>

    <!-- ============================================================ -->
    <!-- TAB: BALANCE -->
    <!-- ============================================================ -->
    <template v-if="activeTab === 'balance'">
      <!-- Bloque COBRANZA: cuánto se esperaba / entró / falta (idéntico a Recaudación) -->
      <div class="stat-strip stat-strip--spaced">
        <div class="stat-strip__item">
          <span class="stat-strip__label">Esperado</span>
          <span class="stat-strip__value">{{ formatUSD(expected) }}</span>
        </div>
        <div class="stat-strip__item">
          <span class="stat-strip__label">Recaudado</span>
          <span class="stat-strip__value stat-strip__value--success">{{ formatUSD(collected) }}</span>
        </div>
        <div class="stat-strip__item">
          <span class="stat-strip__label">Pendiente</span>
          <span class="stat-strip__value" :class="pending > 0 ? 'stat-strip__value--error' : ''">{{ formatUSD(pending) }}</span>
        </div>
      </div>

      <!-- Bloque RESULTADO: desglose de gastos → utilidad del local como línea-cierre -->
      <div class="collection-list">
        <div class="collection-card">
          <div class="balance-breakdown">
            <div class="balance-breakdown__title">Resultado del local</div>
            <div class="balance-line">
              <span class="balance-line__label">Gastos operativos</span>
              <span class="balance-line__value">{{ formatUSD(operativeExpenses) }}</span>
            </div>
            <div class="balance-line">
              <span class="balance-line__label">Prediales</span>
              <span class="balance-line__value">{{ formatUSD(taxExpenses) }}</span>
            </div>
            <div class="balance-line">
              <span class="balance-line__label">Planilla</span>
              <span class="balance-line__value">{{ formatUSD(payrollExpenses) }}</span>
            </div>
            <div class="balance-line balance-line--total">
              <span class="balance-line__label">Total de gastos</span>
              <span class="balance-line__value">{{ formatUSD(totalExpenses) }}</span>
            </div>
            <div class="balance-line balance-line--result">
              <span class="balance-line__label">Utilidad</span>
              <span class="balance-line__value" :class="profit < 0 ? 'balance-line__value--error' : ''">{{ formatUSD(profit) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ============================================================ -->
    <!-- TAB: RECAUDACIÓN -->
    <!-- ============================================================ -->
    <template v-if="activeTab === 'collection'">
      <div class="stat-strip stat-strip--spaced">
        <div class="stat-strip__item">
          <span class="stat-strip__label">Esperado</span>
          <span class="stat-strip__value">{{ formatUSD(expected) }}</span>
        </div>
        <div class="stat-strip__item">
          <span class="stat-strip__label">Recaudado</span>
          <span class="stat-strip__value stat-strip__value--success">{{ formatUSD(collected) }}</span>
        </div>
        <div class="stat-strip__item">
          <span class="stat-strip__label">Pendiente</span>
          <span class="stat-strip__value" :class="pending > 0 ? 'stat-strip__value--error' : ''">{{ formatUSD(pending) }}</span>
        </div>
      </div>

      <ChipsBar :chips="statusChips" v-model="filterStatus"/>

      <div class="filter-bar filter-bar--no-top">
        <SearchInput v-model="search" placeholder="Buscar expositor…" label="Buscar expositor"/>
        <SortSelect :options="sortOptions" v-model="sortBy" />
      </div>

      <div v-if="visibleTenants.length > 0" class="collection-list">
        <div class="collection-card">
          <div v-for="t in visibleTenants" :key="t.id">
            <div
              class="tenant-row"
              :class="[t.overdue ? 'tenant-row--overdue' : '', t.bills.length ? 'tenant-row--clickable' : '']"
              @click="t.bills.length && toggleTenant(t.id)"
            >
              <div class="tenant-row__body">
                <div class="tenant-row__name">{{ t.name }}</div>
                <div class="tenant-row__meta">
                  <template v-if="t.rentUSD">{{ formatUSD(t.rentUSD) }}/mes</template>
                  <template v-else>Sin contrato vigente</template>
                  <span v-if="t.noContract" class="tenant-row__flag">sin contrato</span>
                </div>
              </div>
              <!-- Verificación del comprobante. En la fila va el veredicto que más
                   pide mirar de las letras del mes —la alerta manda sobre lo que
                   coincide—; el detalle letra por letra está al desplegar. -->
              <div v-if="t.worstAudit || t.voucherSummary" class="tenant-row__voucher">
                <VoucherStatus :audit="t.worstAudit" />
                <span v-if="t.voucherSummary" class="tenant-row__detail">{{ t.voucherSummary }}</span>
              </div>
              <div class="tenant-row__right">
                <span v-if="t.pendingUSD > 0" class="tenant-row__amount">{{ formatUSD(t.pendingUSD) }}</span>
                <span class="tenant-row__status" :class="tenantStatusClass(t)">{{ tenantStatusLabel(t) }}</span>
              </div>
              <span v-if="t.bills.length" class="prop-row__chevron"
                    :class="{ 'prop-row__chevron--open': isExpanded(t.id) }">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </span>
            </div>

            <!-- Letras del mes del expositor: el comprobante y su verificación
                 viven en la letra, no en el expositor, así que el detalle se abre
                 aquí y se ve la imagen en el mismo panel que en Letras. -->
            <transition name="expand">
              <div v-if="isExpanded(t.id)" class="collection-sub">
                <div v-for="b in t.bills" :key="b.id" class="bill-row">
                  <div class="bill-row__body">
                    <div class="bill-row__amount">{{ formatAmount(b.total, b.client ? b.client.currency : null) }}</div>
                    <div class="bill-row__meta">
                      Vence {{ formatDate(b.deadline) }}
                      <template v-if="b.paid && b.paymentDate"> · pagada el {{ formatDate(b.paymentDate) }}</template>
                    </div>
                  </div>
                  <div class="bill-row__voucher">
                    <VoucherStatus :audit="b.voucherAudit" />
                    <span class="bill-row__meta">{{ billVoucherLabel(b) }}</span>
                  </div>
                  <span class="badge badge--sm" :class="b.paid ? 'badge--success' : 'badge--error'">
                    {{ b.paid ? 'Pagada' : 'Pendiente' }}
                  </span>
                  <button class="btn btn--outline btn--sm" @click.stop="openPay(b)">{{ payLabel(b) }}</button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
      <EmptyState v-else :title="tenants.length ? 'Sin resultados' : 'Sin expositores activos'"
                  :message="tenants.length ? 'Ningún expositor coincide con los filtros.' : 'No hay contratos vigentes para este período.'"/>
    </template>

    <!-- ============================================================ -->
    <!-- TAB: GASTOS (módulo embebido) -->
    <!-- ============================================================ -->
    <!-- Alta, edición o borrado de un gasto desactualiza los totales del Balance,
         que se calculan con su propia consulta: se marcan viejos y se recargan al volver. -->
    <ExpensesView v-if="activeTab === 'expenses' && estate.id" :embedded="true" :forced-estate="forcedEstate"
                  v-model:period="period" @changed="staleData = true" />

    <!-- ============================================================ -->
    <!-- TAB: IMPUESTOS (módulo embebido) -->
    <!-- ============================================================ -->
    <TaxesView v-if="activeTab === 'taxes' && estate.id" :embedded="true" :estate-id="estate.id" v-model:period="period" />

    <!-- Mismo panel que en Letras: el comprobante, su veredicto y las acciones
         de verificación (dar por revisada, reabrir, volver a verificar). -->
    <PayBill
      :data="payData"
      :show-navbar="showPaySidebar"
      :class="{'show': showPaySidebar}"
      @close="showPaySidebar = false"
      @update="onPayUpdate"
      @audit="onAuditUpdate"
      @added="onBillAdded"
    />
  </div>
</template>

<script>
import { Timestamp, db } from '@/firebase/firebaseConfig'
import HeroHeader from '@/components/HeroHeader'
import TabBar from '@/components/TabBar'
import MonthNav from '@/components/MonthNav'
import ChipsBar from '@/components/ChipsBar'
import SortSelect from '@/components/SortSelect'
import EmptyState from '@/components/EmptyState'
import SearchInput from '@/components/SearchInput'
import ExpensesView from '@/views/expenses/App'
import TaxesView from '@/views/estates/taxes/App'
import VoucherStatus from '@/components/VoucherStatus'
import PayBill from '@/views/bills/Pay'
import axios from 'axios'
import moment from 'moment'
import { currentPeriod, periodRange, inPeriod } from '@/utils/period'
import { sortItems } from '@/utils/sort'
import { normalizeText, matches } from '@/utils/text'

export default {
  name: 'EstateDetail',
  components: { HeroHeader, TabBar, MonthNav, ChipsBar, SortSelect, EmptyState, ExpensesView, TaxesView, SearchInput, VoucherStatus, PayBill },
  data () {
    return {
      estate: {},
      clients: [],
      contracts: [],
      bills: [],
      expenses: [],
      taxes: [],
      payrollNetPEN: 0,
      exchangeRate: 3.7,
      search: '',
      filterStatus: 'all',
      sortBy: 'pending_desc',
      sortOptions: [
        { value: 'pending_desc', label: 'Pendiente (mayor primero)', field: 'pendingUSD', type: 'number', dir: 'desc' },
        { value: 'name', label: 'Expositor (A–Z)', field: 'name', type: 'string', dir: 'asc' },
        { value: 'rent_desc', label: 'Renta (mayor a menor)', field: 'rentUSD', type: 'number', dir: 'desc' },
        { value: 'status', label: 'Estado (pendientes primero)', field: 'paid', type: 'bool', dir: 'asc' }
      ],
      // Mes de la página ('YYYY-MM'): manda sobre las cuatro pestañas
      period: currentPeriod(),
      // Gastos/Impuestos pueden mover el mes sin que Balance esté montado; marca
      // que los datos de la página quedaron viejos para recargarlos al volver.
      staleData: false,
      // Generación de carga: el mes es una flecha y se clickea en ráfaga. Sin este
      // token, dos loadData solapados terminan mezclando las letras de dos meses
      // en el mismo array y el Esperado/Recaudado/Pendiente sale mal.
      loadGen: 0,
      // Expositores desplegados en Recaudación (ids de ficha)
      expandedIds: [],
      showPaySidebar: false,
      // Forma mínima que espera el panel de la letra antes de recibir una
      payData: { client: { currency: { code: '' } } }
    }
  },
  computed: {
    activeTab: {
      get () {
        const tab = this.$route.query.tab
        return ['balance', 'collection', 'expenses', 'taxes'].includes(tab) ? tab : 'balance'
      },
      set (val) {
        this.$router.replace({ query: { tab: val } })
      }
    },
    sectionTabs () {
      return [
        { label: 'Balance', value: 'balance' },
        { label: 'Recaudación', value: 'collection' },
        { label: 'Gastos', value: 'expenses' },
        { label: 'Impuestos', value: 'taxes' }
      ]
    },
    forcedEstate () {
      return { id: this.estate.id, description: this.estate.description }
    },
    // Las pestañas cuyos datos salen de loadData()
    usesPageData () { return this.activeTab === 'balance' || this.activeTab === 'collection' },
    // ---- Recaudación ----
    // Letras de ESTE local con vencimiento en el mes, agrupadas por inquilino. Un inquilino
    // puede tener varias en un mismo mes (cargo extra, segundo puesto, letra partida), así que
    // agrupar es obligatorio: quedarse con la primera daba un estado de pago falso.
    // Letras del mes agrupadas por inquilino, sólo de fichas ACTIVAS de este local. El local
    // sale de la ficha, no del snapshot de la letra (que puede haber quedado viejo).
    billsByClient () {
      const ids = new Set(this.clients.filter(c => c.local && c.local.id === this.estate.id).map(c => c.id))
      const map = {}
      this.bills.forEach(b => {
        const cid = b.client && b.client.id
        if (!cid || !ids.has(cid)) return
        if (!map[cid]) map[cid] = []
        map[cid].push(b)
      })
      return map
    },
    // Todas las letras del mes de los inquilinos activos del local, en una sola lista.
    estateBills () { return Object.values(this.billsByClient).reduce((all, arr) => all.concat(arr), []) },
    tenants () {
      const monthEnd = periodRange(this.period).end
      const now = new Date()
      return this.clients
        .filter(c => c.local && c.local.id === this.estate.id)
        .map(client => {
          const contract = this.getActiveContract(client.id)
          const bills = this.billsByClient[client.id] || []
          // Sin contrato vigente y sin letras del mes no hay nada que mostrar ni que cobrar.
          if (!contract && !bills.length) return null
          const paidAmount = bills.filter(b => b.paid).reduce((s, b) => s + this.billUSD(b), 0)
          const rentUSD = contract ? this.toUSD(contract.rent, contract.currency ? contract.currency.code : 'PEN') : 0
          // Mismo criterio que la cifra de arriba: lo que le falta para su renta del mes.
          const pendingAmount = rentUSD - paidAmount
          return {
            id: client.id,
            name: client.alias || client.name,
            rentUSD,
            paidUSD: paidAmount,
            pendingUSD: pendingAmount,
            paid: pendingAmount <= 0,
            overdue: pendingAmount > 0 && monthEnd < now,
            noBills: bills.length === 0,
            noContract: !contract,
            // Las letras del mes viajan con el expositor: el comprobante y su
            // verificación son de la letra, y ahí se despliegan.
            bills,
            worstAudit: this.worstAudit(bills),
            voucherSummary: this.voucherSummary(bills)
          }
        })
        .filter(Boolean)
    },
    statusChips () {
      return [{ label: 'Todos', value: 'all' }, { label: 'Pendientes', value: 'pending' }, { label: 'Pagados', value: 'paid' }]
    },
    // Los filtros mueven sólo lo que se ve. Las tres cifras de arriba se siguen
    // calculando sobre TODOS los expositores del local: filtrar no mueve una cifra.
    visibleTenants () {
      let list = this.tenants
      if (this.filterStatus === 'pending') list = list.filter(t => !t.paid)
      else if (this.filterStatus === 'paid') list = list.filter(t => t.paid)
      const q = normalizeText(this.search)
      if (q) list = list.filter(t => matches(t.name, q))
      return sortItems(list, this.sortOptions.find(o => o.value === this.sortBy))
    },
    // Esperado = meta del mes: Σ renta de los contratos vigentes de inquilinos ACTIVOS.
    expected () { return this.tenants.reduce((s, t) => s + t.rentUSD, 0) },
    // Recaudado = devengado por VENCIMIENTO: suma del monto de las letras de ESTE local
    // cuyo deadline cae en el mes seleccionado (this.bills) y que están pagadas, convertido
    // a USD con la moneda de la letra. Una letra de junio pagada en julio cuenta en JUNIO.
    collected () {
      return this.estateBills.filter(b => b.paid).reduce((s, b) => s + this.billUSD(b), 0)
    },
    // Pendiente = Esperado − Recaudado. Las dos cifras miran a los mismos inquilinos (activos),
    // así que la resta es lo que falta para llegar a la renta del mes. Puede salir NEGATIVA
    // —se cobró más que la meta: adelantos, letras partidas de otro mes— y así se muestra.
    pending () { return this.expected - this.collected },
    // ---- Balance / Gastos ----
    operativeExpenses () {
      return this.expenses
        .filter(e => e.paid && e.type !== 'Planilla' && e.local && e.local.id === this.estate.id && this.inSelectedMonth(e.deadline))
        // Conversión histórica: usa el TC guardado con el gasto (e.exchangeRate); los
        // gastos viejos sin ese campo caen al TC vivo (retrocompat).
        .reduce((s, e) => s + this.toUSD(e.total, e.currency ? e.currency.code : 'PEN', e.exchangeRate), 0)
    },
    taxExpenses () {
      return this.taxes
        .filter(t => t.paid && this.inSelectedMonth(t.deadline))
        .reduce((s, t) => s + this.toUSD(t.total, t.currency ? t.currency.code : 'PEN'), 0)
    },
    payrollExpenses () {
      return this.toUSD(this.payrollNetPEN, 'PEN')
    },
    totalExpenses () { return this.operativeExpenses + this.taxExpenses + this.payrollExpenses },
    income () { return this.collected },
    profit () { return this.income - this.totalExpenses }
  },
  watch: {
    // Balance y Recaudación son las que consumen loadData(): 6 lecturas, dos de
    // ellas collectionGroup sin filtro. Desde Gastos o Impuestos —que traen lo
    // suyo por su cuenta— sólo se marca como viejo y se recarga al volver.
    period () {
      if (this.usesPageData) this.loadData()
      else this.staleData = true
    },
    activeTab () {
      if (this.usesPageData && this.staleData) this.loadData()
    }
  },
  async created () {
    await this.getEstate()
    // Mismo criterio que el watcher: si se entra directo por ?tab=expenses (F5,
    // bookmark, atrás del navegador) no se cargan los datos del Balance —se
    // marcan como viejos y se traen al abrir la pestaña que los usa.
    if (this.usesPageData) await this.loadData()
    else this.staleData = true
  },
  methods: {
    async getEstate () {
      try {
        this.$store.dispatch('showProgress')
        const doc = await db.collection('estates').doc(this.$route.params.id).get()
        if (doc.exists) this.estate = { id: doc.id, ...doc.data() }
      } catch (e) {
        this.$toast.error('Error al cargar la propiedad')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async loadData () {
      if (!this.estate.id) return
      const gen = ++this.loadGen
      try {
        this.$store.dispatch('showProgress')
        await Promise.all([
          this.getExchangeRate(gen),
          this.getClients(gen),
          this.getContracts(gen),
          this.getBillsForMonth(gen),
          this.getExpensesForMonth(gen),
          this.getTaxes(gen),
          this.getPayroll(gen)
        ])
        // Sólo la carga vigente da los datos por frescos: si ésta quedó atrás la
        // pantalla es de otra, y si reventó el flag tiene que seguir en true para
        // que volver a la pestaña reintente.
        if (gen === this.loadGen) this.staleData = false
      } catch (e) {
        this.$toast.error('Error al cargar datos')
      } finally {
        // Una carga vieja no apaga la barra de la que la reemplazó
        if (this.isCurrent(gen)) this.$store.dispatch('hideProgress')
      }
    },
    // true si esta carga sigue siendo la vigente. Las consultas asignan recién
    // después de resolver, así que una respuesta vieja se descarta entera.
    isCurrent (gen) { return gen === this.loadGen },
    monthRange () { return periodRange(this.period) },
    inSelectedMonth (date) { return inPeriod(date, this.period) },
    async getClients (gen) {
      // REGLA DEL NEGOCIO: sólo cuentan los inquilinos ACTIVOS, y vale para las TRES cifras
      // —esperado, recaudado y pendiente—. Dar de baja una ficha es cómo se saca a un inquilino
      // del cálculo, así que las letras de una ficha de baja no suman en ninguna de las tres
      // (siguen existiendo en su ficha; para que dejen de existir hay que anularlas).
      const list = []
      const snap = await db.collection('clients').where('state', '==', true).get()
      snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }))
      if (this.isCurrent(gen)) this.clients = list
    },
    async getContracts (gen) {
      const list = []
      const snap = await db.collectionGroup('contracts').get()
      snap.forEach(doc => {
        const d = doc.data()
        list.push({
          id: doc.id,
          clientId: doc.ref.parent.parent.id,
          rent: d.rent,
          currency: d.currency,
          start: d.start ? d.start.toDate() : null,
          end: d.end ? d.end.toDate() : null
        })
      })
      if (this.isCurrent(gen)) this.contracts = list
    },
    getActiveContract (clientId) {
      const { start: monthStart, end: monthEnd } = this.monthRange()
      return this.contracts.find(c =>
        c.clientId === clientId && c.start && c.start <= monthEnd && (!c.end || c.end >= monthStart)
      )
    },
    async getBillsForMonth (gen) {
      const list = []
      const { start, end } = this.monthRange()
      const snap = await db.collectionGroup('bills')
        .where('deadline', '>=', Timestamp.fromDate(start))
        .where('deadline', '<=', Timestamp.fromDate(end))
        .orderBy('deadline', 'asc').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline.toDate()
        obj.paymentDate = doc.data().paymentDate ? doc.data().paymentDate.toDate() : null
        list.push(obj)
      })
      if (this.isCurrent(gen)) this.bills = list
    },
    async getExpensesForMonth (gen) {
      const list = []
      const { start, end } = this.monthRange()
      const snap = await db.collection('expenses')
        .where('deadline', '>=', Timestamp.fromDate(start))
        .where('deadline', '<=', Timestamp.fromDate(end))
        .orderBy('deadline', 'asc').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline ? doc.data().deadline.toDate() : null
        list.push(obj)
      })
      if (this.isCurrent(gen)) this.expenses = list
    },
    async getTaxes (gen) {
      const list = []
      const snap = await db.collection('estates').doc(this.estate.id)
        .collection('propertyTaxes').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline ? doc.data().deadline.toDate() : null
        list.push(obj)
      })
      if (this.isCurrent(gen)) this.taxes = list
    },
    async getPayroll (gen) {
      let net = 0
      const doc = await db.doc(`payrollPeriods/${this.period}`).get()
      if (doc.exists) {
        const data = doc.data()
        const byEstate = data.totals && data.totals.byEstate ? data.totals.byEstate : null
        if (byEstate && byEstate[this.estate.id]) net = byEstate[this.estate.id].net || 0
      }
      if (this.isCurrent(gen)) this.payrollNetPEN = net
    },
    async getExchangeRate (gen) {
      try {
        const now = new Date()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const date = String(now.getDate()).padStart(2, '0')
        const res = await axios.post(
          'https://southamerica-east1-inqi-5fe1e.cloudfunctions.net/vendors-getExchangeRate',
          { year: now.getFullYear(), month, date },
          { headers: { Authorization: 'Bearer ' + (process.env.VUE_APP_EXCHANGE_RATE_TOKEN || '78b1deb4-1b27-4943-9d83-818285cc59d6') } }
        )
        if (this.isCurrent(gen)) this.exchangeRate = res.data.venta || 3.7
      } catch { if (this.isCurrent(gen)) this.exchangeRate = 3.7 }
    },
    // rate opcional: si el registro trae su propio TC histórico (>0) se usa ese; si no,
    // cae al TC vivo (this.exchangeRate). USD se devuelve tal cual.
    toUSD (amount, code, rate) {
      if (!amount) return 0
      if (code === 'USD') return amount
      const tc = rate > 0 ? rate : this.exchangeRate
      return tc > 0 ? amount / tc : 0
    },
    formatUSD (val) {
      return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0)
    },
    // Monto de una letra en USD, con la moneda de la propia letra.
    billUSD (b) {
      return this.toUSD(b.total, b.client && b.client.currency ? b.client.currency.code : 'PEN')
    },
    isExpanded (id) { return this.expandedIds.includes(id) },
    toggleTenant (id) {
      const i = this.expandedIds.indexOf(id)
      if (i === -1) this.expandedIds.push(id)
      else this.expandedIds.splice(i, 1)
    },
    formatDate (d) { return moment(d).locale('es').format('DD/MMM/YY') },
    formatAmount (total, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(total || 0)
    },
    // Los comprobantes de una letra sin repetir: los del admin y los que subió
    // el inquilino desde el app pueden ser la misma imagen.
    voucherUrls (b) {
      return [
        ...(b.vouchers || []),
        ...(b.clientVouchers || []),
        ...(b.voucher ? [b.voucher] : [])
      ].filter(Boolean)
    },
    voucherCount (b) { return new Set(this.voucherUrls(b)).size },
    billVoucherLabel (b) {
      const n = this.voucherCount(b)
      if (!n) return 'Sin comprobante'
      const label = n + (n === 1 ? ' comprobante' : ' comprobantes')
      return (b.clientVouchers || []).length ? label + ' · del inquilino' : label
    },
    payLabel (b) {
      if (!b.paid) return 'Registrar pago'
      return this.voucherCount(b) ? 'Ver comprobante' : 'Ver letra'
    },
    // Qué veredicto se muestra en la fila plegada cuando el expositor tiene
    // varias letras en el mes: manda el que exige que alguien haga algo. Una
    // alerta sin cerrar pesa más que una que cuadra, y "revisada" pesa más que
    // "coincide" porque lleva el juicio de una persona encima.
    voucherRank (audit) {
      if (!audit || !audit.status) return -1
      if (audit.resolved) return 1
      if (audit.status === 'alert') return 3
      if (audit.status === 'review') return 2
      return 0
    },
    worstAudit (bills) {
      let audit = null
      let rank = -1
      bills.forEach(b => {
        const r = this.voucherRank(b.voucherAudit)
        if (r > rank) { rank = r; audit = b.voucherAudit }
      })
      return audit
    },
    // Línea bajo el veredicto. Calla cuando no hay nada que decir: un expositor
    // que todavía no paga no tiene por qué anunciar que no hay comprobante.
    voucherSummary (bills) {
      if (!bills.length) return ''
      const urls = new Set()
      bills.forEach(b => this.voucherUrls(b).forEach(u => urls.add(u)))
      const head = bills.length > 1 ? bills.length + ' letras · ' : ''
      if (!urls.size) return bills.some(b => b.paid) ? head + 'sin comprobante' : ''
      return head + urls.size + (urls.size === 1 ? ' comprobante' : ' comprobantes')
    },
    openPay (bill) {
      this.payData = { ...bill }
      this.showPaySidebar = true
    },
    // El panel devuelve el cambio ya guardado: se aplica sobre la letra que ya
    // está en memoria en vez de volver a leer el mes entero, y las tres cifras
    // de arriba se recalculan solas.
    applyBillPatch (patch) {
      if (!patch || !patch.id) return
      const idx = this.bills.findIndex(b => b.id === patch.id)
      if (idx !== -1) Object.assign(this.bills[idx], patch)
    },
    onPayUpdate (patch) {
      this.applyBillPatch(patch)
      this.showPaySidebar = false
    },
    onAuditUpdate (patch) { this.applyBillPatch(patch) },
    onBillAdded (newBill) {
      // La letra por la diferencia puede vencer en otro mes: sólo entra en la
      // lista —y en las cifras— si cae en el que se está viendo.
      if (inPeriod(newBill.deadline, this.period)) this.bills.push(newBill)
      this.showPaySidebar = false
    },
    tenantStatusClass (t) {
      if (t.noBills) return 'tenant-row__status--expiring'
      if (t.paid) return 'tenant-row__status--ok'
      if (t.overdue) return 'tenant-row__status--overdue'
      return 'tenant-row__status--expiring'
    },
    tenantStatusLabel (t) {
      if (t.noBills) return 'Sin letra'
      if (t.paid) return 'Pagado'
      if (t.overdue) return 'Vencido'
      return 'Pendiente'
    }
  }
}
</script>

<style scoped>
.tenant-row__flag {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #9F1239;
  background: #FFE4E6;
  border-radius: 4px;
}
.tenant-row__amount {
  margin-right: 10px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: #B91C1C;
}
.tenant-detail-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 24px 0;
}
.tenant-detail-meta__sep {
  font-size: 12px;
  color: #D4D4D8;
}
.tenant-detail-meta__item {
  font-size: 12px;
  color: #A1A1AA;
}
</style>
